import {
  IGroupValidationResult,
  IInputConfig,
  Input,
  InputGroup,
  TextArea,
} from "component-library";
import { useEffect, useState } from "react";

import {
  GET_VALIDATION_DEFAULT,
  getValidationFunction,
  IEmailFormContent,
  IEmailForm,
  IEmailFormValidation,
  MAX_MESSAGE_LENGTH,
  VALIDATE,
} from "./helpers";

import "./EmailForm.scss";

export interface IEmailFormProps {
  updateForm: (form: IEmailForm) => void;
  updateFormValidity: (isFormValid: boolean) => void;
  initialForm: IEmailForm;
  content: IEmailFormContent;
  isFormSubmitted: boolean;
}

const EmailForm = ({
  updateForm,
  updateFormValidity,
  initialForm,
  content,
  isFormSubmitted,
}: IEmailFormProps) => {
  const [form, setForm] = useState<IEmailForm>(() => ({ ...initialForm }));
  const [validationData, setValidationData] = useState<IEmailFormValidation>({
    message: GET_VALIDATION_DEFAULT(),
    subject: GET_VALIDATION_DEFAULT(),
    recipients: GET_VALIDATION_DEFAULT(),
    senderName: GET_VALIDATION_DEFAULT(),
    senderEmail: GET_VALIDATION_DEFAULT(),
  });

  const validationFunctions = getValidationFunction(content);

  const formConfig: IInputConfig[] = (
    Object.keys(form) as (keyof IEmailForm)[]
  ).map((key) => ({
    name: key,
    value: form[key] || "",
    validators: validationFunctions[key],
  }));

  const inputChanged = (key: keyof IEmailForm) => (value: string) => {
    const updatedForm = { ...form, [key]: value };
    setForm(updatedForm);
    updateForm(updatedForm);
  };

  const renderInput = (
    key: keyof Omit<IEmailForm, "message">,
    label: string
  ) => (
    <Input
      id={key}
      name={key}
      type="text"
      label={label}
      value={form[key]}
      valueChanged={inputChanged(key)}
      invalid={validationData[key].invalid}
      isFormSubmitted={isFormSubmitted}
      validationMessage={validationData[key].validationMessage}
    />
  );

  const validationPerformed = (result: IGroupValidationResult) => {
    const newValidationData = {
      ...validationData,
      ...result.validationData,
    };
    setValidationData(newValidationData);
    updateFormValidity(!result.isGroupInvalid);
  };

  useEffect(() => {
    VALIDATE.call();
  }, [form]);

  return (
    <InputGroup
      className="c-email-form"
      inputConfigs={formConfig}
      validationDone={validationPerformed}
      triggerValidation={VALIDATE}
    >
      {renderInput("senderName", content.emailFormSenderNameLabel)}
      {renderInput("senderEmail", content.emailFormSenderEmailLabel)}
      {renderInput("recipients", content.emailFormRecipientsLabel)}
      {renderInput("subject", content.emailFormSubjectLabel)}
      <TextArea
        id="emailFormTextArea"
        label={content.emailFormMessageLabel}
        value={form.message}
        valueChanged={inputChanged("message")}
        autoResize
        showsCharacterCount
        maxLength={MAX_MESSAGE_LENGTH}
      />
      <div className="c-email-form__additional-text">
        {content.emailFormAdditionalText}
      </div>
    </InputGroup>
  );
};

export default EmailForm;
